import React, { useState } from 'react';
import { bankAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { Upload, Download, FileText } from 'lucide-react';
import { canEdit } from '@/lib/auth';

export default function Bank() {
  const [returnFile, setReturnFile] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [result, setResult] = useState(null);

  const handleProcessReturn = async (e) => {
    e.preventDefault();
    if (!returnFile) return;
    
    setProcessing(true);
    try {
      const response = await bankAPI.processReturn(returnFile);
      setResult(response.data);
      toast.success('Retorno processado com sucesso!');
    } catch (error) {
      toast.error('Erro ao processar retorno');
    } finally {
      setProcessing(false);
    }
  };

  const handleDownloadRemittance = async () => {
    toast.info('Funcionalidade em desenvolvimento');
  };

  return (
    <div className="p-6 space-y-6" data-testid="bank-page">
      <div>
        <h1 className="text-4xl font-bold tracking-tight">Operações Bancárias</h1>
        <p className="text-muted-foreground mt-1">Processamento de retorno e geração de remessa CNAB 400</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Processar Retorno */}
        <Card className="border-border/60">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Upload className="w-5 h-5 text-primary" />
              Processar Retorno Safra
            </CardTitle>
            <CardDescription>Upload arquivo CNAB 400 retorno</CardDescription>
          </CardHeader>
          <CardContent>
            {canEdit() ? (
              <form onSubmit={handleProcessReturn} className="space-y-4">
                <div>
                  <Input
                    type="file"
                    accept=".ret,.txt"
                    onChange={(e) => setReturnFile(e.target.files[0])}
                    data-testid="return-file-input"
                  />
                </div>
                <Button type="submit" disabled={!returnFile || processing} className="w-full" data-testid="process-return-button">
                  <FileText className="w-4 h-4 mr-2" />
                  {processing ? 'Processando...' : 'Processar Retorno'}
                </Button>
              </form>
            ) : (
              <p className="text-sm text-muted-foreground">Você não tem permissão para esta operação</p>
            )}
            
            {result && (
              <div className="mt-6 p-4 bg-muted rounded-md space-y-2">
                <h4 className="font-semibold">Resultado do Processamento:</h4>
                <div className="text-sm space-y-1">
                  <p>Total de registros: <span className="font-medium">{result.total_records}</span></p>
                  <p className="text-green-600">Processados com sucesso: <span className="font-medium">{result.successful_records}</span></p>
                  <p className="text-red-600">Falhas: <span className="font-medium">{result.failed_records}</span></p>
                  <p className="text-yellow-600">Pendente conciliação: <span className="font-medium">{result.pending_reconciliation}</span></p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Gerar Remessa */}
        <Card className="border-border/60">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Download className="w-5 h-5 text-secondary" />
              Gerar Remessa Safra
            </CardTitle>
            <CardDescription>Download arquivo CNAB 400 remessa</CardDescription>
          </CardHeader>
          <CardContent>
            {canEdit() ? (
              <div className="space-y-4">
                <p className="text-sm text-muted-foreground">
                  Gera arquivo de remessa com todos os títulos pendentes que ainda não foram enviados ao banco.
                </p>
                <Button onClick={handleDownloadRemittance} className="w-full" data-testid="generate-remittance-button">
                  <Download className="w-4 h-4 mr-2" />
                  Gerar e Baixar Remessa
                </Button>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">Você não tem permissão para esta operação</p>
            )}
          </CardContent>
        </Card>
      </div>

      <Card className="border-primary/20 bg-primary/5">
        <CardHeader>
          <CardTitle className="text-lg">Sobre CNAB 400 Safra</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3 text-sm text-muted-foreground">
            <p>
              <strong>Retorno:</strong> Arquivo enviado pelo banco Safra contendo informações sobre pagamentos realizados.
              O sistema identifica os títulos pelo "Nosso Número" e dá baixa automática.
            </p>
            <p>
              <strong>Remessa:</strong> Arquivo gerado pelo sistema para envio ao banco Safra contendo os títulos
              a serem cobrados. Cada título recebe um "Nosso Número" único.
            </p>
            <div className="bg-background p-3 rounded-md border border-border">
              <p className="font-medium mb-2">Campos principais processados:</p>
              <ul className="list-disc list-inside space-y-1">
                <li>Nosso Número (posição 63-71)</li>
                <li>Data de pagamento (posição 111-116)</li>
                <li>Valor pago (posição 254-266)</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}